'use client';

import { Extension } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { PluginKey } from '@tiptap/pm/state';
import { type Editor, ReactRenderer } from '@tiptap/react';
import tippy, { type Instance as TippyInstance } from 'tippy.js';
import { createCustomSuggestionPlugin } from '@/app/components/shared/MentionsTextarea/extensions/custom-suggestion';
import type { ClawSkillItem } from '../../../../_hook/use-claw-skill-search';
import { SkillSuggestionList } from '../SkillSuggestionList';

export const ClawSkillSuggestionPluginKey = new PluginKey('claw-skill-suggestion');

export interface ClawSkillSuggestionOptions {
  /** 远端搜索平台 skill，query 为 `/` 之后输入的关键字。 */
  searchSkills: (query: string) => Promise<ClawSkillItem[]>;
  /** 选中某个 skill 时回调，业务侧据此把 skill chip 插入草稿。 */
  onSelectSkill: (item: ClawSkillItem) => void;
  /** 提示框 z-index，默认与 mention 一致。 */
  zIndex?: number;
  /** 返回 false 时禁止触发 skill suggestion（例如 yolo 模式下）。 */
  isEnabled?: () => boolean;
}

/**
 * 当编辑器内容仅为 `/关键字` 时为 true：
 * - 不含 mention / claw-template-token 等原子节点；
 * - 纯文本恰好等于当前 suggestion 匹配文本（以 `/` 开头）。
 * 满足「输入框没有内容时输入 / 唤起 skill」，一旦另起内容或已有 chip 就不再触发。
 */
const isEmptyExceptSlash = (doc: ProseMirrorNode, matchText: string): boolean => {
  let hasAtomNode = false;
  doc.descendants((node) => {
    if (node.type.name === 'mention' || node.type.name === 'claw-template-token') {
      hasAtomNode = true;
      return false;
    }
    return true;
  });
  const docText = doc.textContent.trim();
  const result = !hasAtomNode && docText === matchText;
  return result;
};

const createSafeGetReferenceClientRect = (clientRect: (() => DOMRect | null) | null): (() => DOMRect) => {
  return () => {
    if (!clientRect) return new DOMRect(0, 0, 0, 0);
    return clientRect() || new DOMRect(0, 0, 0, 0);
  };
};

export const createClawSkillSuggestionExtension = (options: ClawSkillSuggestionOptions) =>
  Extension.create<ClawSkillSuggestionOptions>({
    name: 'claw-skill-suggestion',

    addOptions() {
      return options;
    },

    addProseMirrorPlugins() {
      const editor = this.editor as Editor;
      const { searchSkills, onSelectSkill, zIndex = 49, isEnabled } = this.options;

      return [
        createCustomSuggestionPlugin<ClawSkillItem, ClawSkillItem>({
          editor,
          pluginKey: ClawSkillSuggestionPluginKey,
          char: '/',
          startOfLine: true,
          allowSpaces: false,
          allowedPrefixes: null,
          items: async ({ query }) => searchSkills(query),
          allow: ({ state, range }) => {
            // yolo 模式下不允许触发 skill suggestion。
            if (isEnabled && !isEnabled()) return false;
            // range.from-1 取到触发字符 `/`；用整段文本判断是否「除 / 外无内容」。
            const matchText = state.doc.textBetween(Math.max(0, range.from - 1), range.to, '\n', '\n');
            return isEmptyExceptSlash(state.doc, matchText);
          },
          command: ({ editor: ed, range, props }) => {
            if (!props?.id) return;
            ed.chain()
              .focus()
              .deleteRange(range)
              .insertContent([
                {
                  type: 'claw-template-token',
                  // value 存 skill 运行时 name：发送序列化时据此 hoist 出 `{ type: 'skill' }` 内容，
                  // 与 quick-prompt 的 [skill:label:name] 路径口径一致。
                  // source 据来源分组透传：installed 必须带，否则后端当平台 skill 去 OSS 找不到。
                  attrs: {
                    kind: 'skill',
                    id: props.id,
                    label: props.label,
                    value: props.name,
                    source: props.group === 'installed' ? 'installed' : 'platform',
                  },
                },
                { type: 'text', text: ' ' },
              ])
              .run();
            onSelectSkill(props);
          },
          render: () => {
            let reactRenderer: ReactRenderer | null = null;
            let popup: TippyInstance[] | null = null;

            return {
              onStart: (props) => {
                if (!props.clientRect) return;
                reactRenderer = new ReactRenderer(SkillSuggestionList, {
                  props,
                  editor: props.editor,
                });
                popup = [
                  tippy(document.querySelector('body') as Element, {
                    getReferenceClientRect: createSafeGetReferenceClientRect(props.clientRect),
                    appendTo: () => document.body,
                    content: reactRenderer.element,
                    showOnCreate: true,
                    interactive: true,
                    trigger: 'manual',
                    placement: 'bottom-start',
                    theme: 'dark',
                    zIndex,
                    maxWidth: 720,
                  }),
                ];
              },
              onUpdate: (props) => {
                if (!reactRenderer) return;
                if (!props.clientRect) {
                  popup?.[0]?.hide();
                  return;
                }
                reactRenderer.updateProps(props);
                popup?.[0]?.setProps({
                  getReferenceClientRect: createSafeGetReferenceClientRect(props.clientRect),
                });
              },
              onKeyDown: ({ event }: { event: KeyboardEvent }) => {
                if (event.key === 'Escape') {
                  if (popup?.[0]) {
                    popup[0].hide();
                    event.stopPropagation();
                    return true;
                  }
                }
                if (reactRenderer?.ref) {
                  return (reactRenderer.ref as { onKeyDown: (e: { key: string }) => boolean }).onKeyDown({
                    key: event.key,
                  });
                }
                return false;
              },
              onExit: () => {
                popup?.[0]?.destroy();
                popup = null;
                reactRenderer?.destroy();
                reactRenderer = null;
              },
            };
          },
        }),
      ];
    },
  });
